import NovelNovemberSprint from "./admin/NovelNovemberSprint";
import React, { useEffect, useState, useRef, useCallback } from "react";
import firebase from "firebase/app";
import { Route, Redirect, Switch, NavLink, useParams, useHistory, useLocation } from "react-router-dom";
import { ExternalLinkIcon } from "@radix-ui/react-icons";
import Auth from "./Auth";
import { useAdminAccess } from "./hooks/useAdminAccess";
import { useAdminAccessStore } from "./stores/adminAccessStore";
import { filterAdminNavigation, permissionForAdminPath } from "./admin/adminNavigation";
import AdminRoles from "./admin/AdminRoles";
import { logAdminView } from "./Utils";

import Users from "./admin/Users";
import ConferenceReport from "./admin/ConferenceReport";
import Likes from "./admin/Likes";
import DislikedCards from "./admin/DislikedCards";
import User from "./admin/User";
import Invocations from "./admin/Invocations";
import Document from "./admin/Document";
import CardSet from "./admin/CardSet";
import { CenterBox } from "./Utils";
import UserCache from "./admin/UserCache";
import Completion from "./admin/Completion";
import Canvas from "./components/Canvas";
import Invocation from "./admin/Invocation";
import StoryEngineGate from "./admin/StoryEngineGate";
import SSETest from "./components/Admin/SSETest";
import BulkEditUsers from "./admin/BulkEditUsers";
import Chats from "./admin/Chats";
import Playground from "./admin/Playground";
import Gates from "./admin/Gates";
import { ClientVersions } from "./admin/ClientVersions";
import FeaturedPlugins from "./admin/FeaturedPlugins";
import NewUserInterview from "./admin/NewUserInterview";
import IntegrationTests from "./admin/integrationTests";
import BrainstormTester from "./admin/BrainstormTester";
import ImageGenTester from "./admin/ImageGenTester";
import AdminLogs from "./admin/AdminLogs";
import SharedDocumentsLookup from "./admin/SharedDocumentsLookup";
import { Text } from "./sudokit/components/Text";
import VStack from "./sudokit/Layout/VStack";
import StreamReplayPage from "./admin/StreamReplayPage";
import DuplicatedStripeAccounts from "./admin/DuplicatedStripeAccounts";
import ModelCreditMarkup from "./admin/ModelCreditMarkup";
import SeoTokenUsage from "./admin/SeoTokenUsage";
import ImportNovelTracking from "./admin/ImportNovelTracking";
import PartnerCodes from "./admin/PartnerCodes";
import DiscordUpload from "./admin/DiscordUpload";
import FacebookDeletionTester from "./admin/FacebookDeletionTester";
import PluginRepair from "./admin/PluginRepair";
import Experiments from "./admin/Experiments";
import ExperimentDetail from "./admin/ExperimentDetail";
import AgenticChatFeedback from "./admin/AgenticChatFeedback";
import AgenticChatBugReports from "./admin/AgenticChatBugReports";
import ChatExperiments from "./admin/ChatExperiments";
import ChatControls from "./admin/ChatControls";
import ChatExperienceSurvey from "./admin/ChatExperienceSurvey";
import FeedbackDocumentSplit from "./admin/FeedbackDocumentSplit";
import { Spinner } from "./sudokit/components/Spinner";
const DraftEstimates = React.lazy(() => import("./admin/DraftEstimates"));
const AiCostDashboard = React.lazy(() => import("./admin/AiCostDashboard"));
const CliApiKeys = React.lazy(() => import("./admin/CliApiKeys"));

const AiCostDashboardFallback = ({ title = "AI Cost Dashboard" }) => (
  <section
    className="w-full mx-auto px-4 pt-1 pb-11 box-border"
    style={{ maxWidth: 1600 }}
    aria-label={`Loading ${title}`}
  >
    <header className="flex items-start justify-between gap-4 flex-wrap mt-1 mb-3">
      <div>
        <h1 className="m-0 text-2xl font-semibold leading-tight">{title}</h1>
        <p className="mt-1 mb-0 text-xs font-semibold tracking-widest text-app-grey-1">ADMIN · UTC</p>
      </div>
      <div className="flex items-center gap-1 rounded-lg border border-app-divider px-3 py-1 text-sm text-app-grey-1">
        <Spinner
          size="sm"
          label={`Loading ${title}`}
        />
        <span>Loading analytics…</span>
      </div>
    </header>
  </section>
);

// NavLink styling constants
const NAV_LINK_CLASSES = "block px-3 py-1 mb-1 text-app-grey-0 hover:text-app-grey-0 hover:bg-app-list-element-hover text-base rounded";
const NAV_EXTERNAL_LINK_CLASSES = "flex items-center justify-between gap-2 px-3 py-1 mb-1 text-app-grey-0 hover:text-app-grey-0 hover:bg-app-list-element-hover text-base rounded";
const NAV_LINK_ACTIVE_CLASSES = "bg-app-selected-element-background text-app-selected-element-text font-medium";
const NAV_SECTION_HEADING_CLASSES = "px-3 pt-4 pb-1 text-xs font-semibold uppercase text-app-grey-1";
const ADMIN_NAV_SECTION_STORAGE_KEY = "admin-nav-open-sections";

const ADMIN_NAV_SECTIONS = [
  {
    title: "Core",
    items: [
      { name: "Invocations / Chats", path: "/admin" },
      { name: "Users", path: "/admin/users" },
      { name: "Admin Roles", path: "/admin/roles" },
      { name: "Partner Codes", path: "/admin/partner-codes" },
      { name: "AI Functions", path: "/ai-functions" },
    ],
  },
  {
    title: "Users & Billing",
    items: [
      { name: "Bulk Edit Users", path: "/admin/bulk-edit-users" },
      { name: "Author Nation 2026", path: "/admin/conferences/author-nation-2026" },
      { name: "Client Versions", path: "/admin/clientVersions" },
      { name: "New User Interviews", path: "/admin/newUserInterviews" },
      { name: "Duplicate Stripe Accounts", path: "/admin/duplicate-stripe-accounts" },
      { name: "Import Novel Tracking", path: "/admin/importNovelTracking" },
      { name: "Facebook Account Deletion", path: "/admin/facebook-deletion" },
      { name: "CLI API Keys", path: "/admin/cli-api-keys" },
    ],
  },
  {
    title: "Product Tools",
    items: [
      { name: "Playground", path: "/admin/playground" },
      { name: "Gates", path: "/admin/gates" },
      { name: "Novel November sprints", path: "/admin/novel-november" },
      { name: "Shared Documents", path: "/admin/shared-documents" },
      { name: "Model Credit Markup", path: "/admin/model-credit-markup" },
      { name: "SEO Token Usage", path: "/admin/seo-token-usage" },
      { name: "Discord Upload", path: "/admin/discord-upload" },
      { name: "Plugin Repair", path: "/admin/plugin-repair" },
      { name: "Feedback Doc Split", path: "/admin/feedback-document-split" },
      { name: "Card Experiments", path: "/admin/experiments" },
      { name: "Chat Feedback", path: "/admin/chat-feedback" },
      { name: "Chat Bug Reports", path: "/admin/chat-bug-reports" },
      { name: "Chat Experiments", path: "/admin/chat-experiments" },
      { name: "Chat Controls", path: "/admin/chat-controls" },
      { name: "Chat Experience Survey", path: "/admin/chat-experience-survey" },
    ],
  },
  {
    title: "QA & Diagnostics",
    items: [
      { name: "AI Cost Dashboard", path: "/admin/ai-cost" },
      { name: "Draft Estimates", path: "/admin/draft-estimates" },
      { name: "Integration Tests", path: "/admin/integrationTests" },
      { name: "Admin Logs", path: "/admin/logs", permission: "admin.tools" },
      { name: "Brainstorm Tester", path: "/admin/brainstorm-tester" },
      { name: "Image Gen Tester", path: "/admin/image-gen-tester" },
      { name: "Stream Replay", path: "/admin/stream-replay" },
    ],
  },
  {
    title: "External",
    items: [
      { name: "Storybook", href: "https://storybook.sudodev.io", external: true },
      { name: "Automato Dashboard", action: "automato", external: true },
    ],
  },
];


function AdminNavSection({ title, items, onNavClick, onAutomatoClick }) {
  const location = useLocation();
  const hasActiveItem = items.some((item) => {
    if (!item.path) return false;
    if (item.path === "/admin") return location.pathname === item.path;
    return location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
  });
  const [isOpen, setIsOpen] = useState(() => {
    const defaultOpen = title === "Core" || title === "External" || hasActiveItem;
    try {
      const stored = JSON.parse(localStorage.getItem(ADMIN_NAV_SECTION_STORAGE_KEY) || "{}");
      return stored[title] ?? defaultOpen;
    } catch {
      return defaultOpen;
    }
  });

  useEffect(() => {
    if (hasActiveItem) {
      setIsOpen(true);
    }
  }, [hasActiveItem]);

  const toggleSection = () => {
    setIsOpen((open) => {
      const nextOpen = !open;
      try {
        const stored = JSON.parse(localStorage.getItem(ADMIN_NAV_SECTION_STORAGE_KEY) || "{}");
        localStorage.setItem(
          ADMIN_NAV_SECTION_STORAGE_KEY,
          JSON.stringify({
            ...stored,
            [title]: nextOpen,
          }),
        );
      } catch {
        // Ignore localStorage failures; the nav should still be usable.
      }
      return nextOpen;
    });
  };

  return (
    <div>
      <button
        type="button"
        className={`${NAV_SECTION_HEADING_CLASSES} flex w-full items-center justify-between appearance-none border-0 bg-transparent text-left`}
        onClick={toggleSection}
        aria-expanded={isOpen}
      >
        <span>{title}</span>
        <span className="text-lg leading-none">{isOpen ? "-" : "+"}</span>
      </button>
      {isOpen &&
        items.map((item) => {
          if (item.action === "automato") {
            return (
              <button
                key={item.name}
                type="button"
                onClick={onAutomatoClick}
                className={`${NAV_EXTERNAL_LINK_CLASSES} w-full appearance-none border-0 bg-transparent text-left`}
              >
                <span>{item.name}</span>
                <ExternalLinkIcon aria-hidden />
              </button>
            );
          }

          if (item.href) {
            return (
              <a
                key={item.name}
                href={item.href}
                target="_blank"
                rel="noreferrer"
                className={NAV_EXTERNAL_LINK_CLASSES}
                onClick={onNavClick}
              >
                <span>{item.name}</span>
                <ExternalLinkIcon aria-hidden />
              </a>
            );
          }

          return (
            <NavLink
              key={item.path}
              exact
              to={item.path}
              className={NAV_LINK_CLASSES}
              activeClassName={NAV_LINK_ACTIVE_CLASSES}
              onClick={onNavClick}
            >
              {item.name}
            </NavLink>
          );
        })}
    </div>
  );
}

// Command menu component
function CommandMenu({ isOpen, onClose, navItems, navigate, can }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  // Reset search term when menu is opened or closed
  useEffect(() => {
    if (isOpen) {
      setSearchTerm("");
      setResults(navItems);
    }
  }, [isOpen, navItems]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle closing the menu
  const handleClose = () => {
    setSearchTerm("");
    onClose();
  };

  // Handle search input changes - using prefix search
  useEffect(() => {
    if (searchTerm) {
      // Check if it's a user search command
      if (searchTerm.toLowerCase().startsWith("user ") || searchTerm.toLowerCase().startsWith("u ")) {
        // Extract search query - check which prefix was used and substring accordingly
        const searchQuery = searchTerm.toLowerCase().startsWith("user ") ? searchTerm.substring(5).trim() : searchTerm.substring(2).trim();

        if (searchQuery) {
          setResults([
            {
              name: `Search for user: "${searchQuery}"`,
              path: `/admin/users?q=${encodeURIComponent(searchQuery)}`,
              isCommand: true,
            },
          ]);
          return;
        }
      }

      // Check if it's an invocation search command
      if (can("invocations.readContent") && (searchTerm.toLowerCase().startsWith("invocation ") || searchTerm.toLowerCase().startsWith("i "))) {
        // Extract search query - check which prefix was used and substring accordingly
        const searchQuery = searchTerm.toLowerCase().startsWith("invocation ") ? searchTerm.substring(11).trim() : searchTerm.substring(2).trim();

        if (searchQuery) {
          setResults([
            {
              name: `Search for invocation: "${searchQuery}"`,
              path: `/admin?q=${encodeURIComponent(searchQuery)}`,
              isCommand: true,
            },
          ]);
          return;
        }
      }

      // Regular menu item filtering
      const term = searchTerm.toLowerCase();
      const filteredResults = navItems.filter((item) => item.name.toLowerCase().startsWith(term));
      setResults(filteredResults);
    } else {
      setResults(navItems);
    }
    setSelectedIndex(0);
  }, [searchTerm, navItems]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prevIndex) => (prevIndex + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prevIndex) => (prevIndex - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      if (results[selectedIndex]) {
        navigate(results[selectedIndex].path);
        handleClose();
      }
    } else if (e.key === "Escape") {
      handleClose();
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current && results.length > 0) {
      const selectedElement = resultsRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-20"
      onClick={handleClose}
    >
      <div
        className="w-96 bg-app-document-background rounded-lg shadow-lg overflow-hidden max-h-96 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-2 border-b border-app-divider">
          <input
            ref={inputRef}
            type="text"
            className="w-full px-3 py-2 bg-app-menu-color-background border border-app-grey-2 rounded focus:outline-none focus:ring-1 focus:ring-app-selected-element-background shadow-none appearance-none"
            placeholder="Search or type 'user searchterm' (or 'u searchterm') or 'invocation searchterm' (or 'i searchterm')"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div
          className="overflow-y-auto flex-1"
          ref={resultsRef}
        >
          {results.length > 0 ? (
            <ul className="py-1">
              {results.map((item, index) => (
                <li
                  key={item.path}
                  data-index={index}
                  className={`px-3 py-2 cursor-pointer ${index === selectedIndex ? "bg-app-selected-element-background text-app-selected-element-text" : "hover:bg-app-list-element-hover"} ${
                    item.isCommand ? "font-medium" : ""
                  }`}
                  onClick={() => {
                    navigate(item.path);
                    handleClose();
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  {item.name}
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-3 text-app-grey-0">No results found</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Admin({ user, match }) {
  const { can, loading, authUid, role } = useAdminAccess();
  const authorized = can("admin.access");
  const location = useLocation();
  const navSections = filterAdminNavigation(ADMIN_NAV_SECTIONS, can);
  const navItems = navSections.flatMap(section => section.items).filter(item => item.path);
  const userCache = React.useMemo(() => new UserCache(), [user?.uid, role]);
  const [commandMenuOpen, setCommandMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const history = useHistory();

  // Toggle sidebar
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Close sidebar when navigation occurs on mobile
  const handleNavClick = () => {
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  // Open the automato dashboard in a new tab via the SSO bridge.
  // Server (`POST /api/admin/automato-sso-link`) mints an HMAC-signed URL
  // we hand off to automato; automato verifies, sets its own session
  // cookie, and lands us in the dashboard with no basic-auth prompt.
  // Opens via window.open so the operator's current admin tab stays put.
  const openAutomatoDashboard = useCallback(async () => {
    try {
      const data = await Auth.fetchWithIdToken("/api/admin/automato-sso-link", {});
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else {
        window.alert(
          data?.error ?? "Could not generate an automato sign-in link. Check the server config.",
        );
      }
    } catch (err) {
      console.error("automato SSO link failed:", err);
      window.alert("Could not open the automato dashboard. See console for details.");
    }
    handleNavClick();
  }, []);

  // Handle cmd+k shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCommandMenuOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Navigate to a path
  const navigate = useCallback(
    (path) => {
      history.push(path);
    },
    [history]
  );

  // Recheck on entry and browser focus. Express still checks every action,
  // so an open tab cannot retain privileges after a role downgrade.
  useEffect(() => {
    const refresh = () => useAdminAccessStore.getState().load(user?.uid);
    refresh();
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [user?.uid]);

  if (loading || authUid !== user?.uid) {
    return <CenterBox>Loading...</CenterBox>;
  }

  if ((!loading && !user) || !authorized) {
    return <Redirect to="/" />;
  }

  if (location.pathname === "/admin" && !can("invocations.readContent")) return <Redirect to="/admin/users" />;

  return (
    <>
      <div
        id="admin"
        className="flex text-left h-screen overflow-hidden fixed inset-0"
      >
        {/* Header bar - always visible on small screens */}
        <div className="md:hidden fixed top-0 left-0 right-0 h-12 bg-app-menu-color-background z-20 flex items-center px-3 border-b border-app-divider">
          <button
            type="button"
            onClick={toggleSidebar}
            className="p-2 text-app-grey-0"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
          <Text className="font-bold">Sudowrite Admin</Text>
        </div>

        {/* Overlay to close sidebar when clicked outside */}
        {sidebarOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
            onClick={toggleSidebar}
          />
        )}

        {/* Left sidebar menu - hidden on small screens unless toggled */}
        <div
          className={`w-56 bg-app-menu-color-background border-r border-app-divider flex flex-col overflow-hidden fixed md:relative inset-y-0 left-0 transform ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } md:translate-x-0 transition duration-200 ease-in-out z-10`}
        >
          {/* Only show this header on medium and larger screens */}
          <div className="p-3 border-b border-app-divider hidden md:block">
            <Text className="font-bold">Sudowrite Admin</Text>
          </div>
          {/* Add top padding on mobile to account for the fixed header */}
          <VStack
            className="overflow-y-auto flex-1 md:pt-0 pt-12"
            spacing={0}
            px={3}
            py={1}
          >
            {navSections.map((section) => (
              <AdminNavSection
                key={section.title}
                title={section.title}
                items={section.items}
                onNavClick={handleNavClick}
                onAutomatoClick={openAutomatoDashboard}
              />
            ))}
          </VStack>
        </div>

        {/* Main content area - adjusted to take full width on small screens */}
        <div className="flex-1 p-3 bg-app-document-background overflow-y-auto md:ml-0 transition-all duration-200 ease-in-out md:pt-3 pt-14">
          {can(permissionForAdminPath(location.pathname)) ? <Switch>
            <Route exact path="/admin/roles"><AdminRoles /></Route>
            <Route exact path="/admin/conferences/author-nation-2026"><ConferenceReport /></Route>
            <Route
              exact
              path="/admin"
            >
              <Invocations userCache={userCache} />
            </Route>

            <Route path="/admin/sse-test">
              <SSETest />
            </Route>

            <Route path="/admin/playground">
              <Playground />
            </Route>

            <Route path="/admin/featured-plugins">
              <FeaturedPlugins />
            </Route>

            <Route
              exact
              path="/admin/users"
            >
              <Users userCache={userCache} />
            </Route>

            <Route
              exact
              path="/admin/partner-codes"
            >
              <PartnerCodes />
            </Route>

            <Route exact path="/admin/draft-estimates">
              <React.Suspense fallback={<AiCostDashboardFallback title="Estimate monitoring" />}><DraftEstimates /></React.Suspense>
            </Route>
            <Route exact path="/admin/ai-cost">
              <React.Suspense fallback={<AiCostDashboardFallback />}>
                <AiCostDashboard />
              </React.Suspense>
            </Route>

            <Route
              exact
              path="/admin/clientVersions"
            >
              <ClientVersions />
            </Route>
            <Route
              exact
              path="/admin/newUserInterviews"
            >
              <NewUserInterview />
            </Route>

            <Route
              exact
              path="/admin/chats"
            >
              <Chats userCache={userCache} />
            </Route>

            <Route path="/admin/user/:emailOrId">
              <User userCache={userCache} />
            </Route>

            <Route path="/admin/card-set/:id">
              <CardSet userCache={userCache} />
            </Route>

            <Route path="/admin/completion/:id">
              <Completion userCache={userCache} />
            </Route>

            <Route path="/admin/story-engine-gate">
              <StoryEngineGate userCache={userCache} />
            </Route>

            <Route path="/admin/novel-november">
              <NovelNovemberSprint />
            </Route>

            <Route path="/admin/gates">
              <Gates userCache={userCache} />
            </Route>

            <Route path="/admin/bulk-edit-users">
              <BulkEditUsers userCache={userCache} />
            </Route>

            <Route path="/admin/invocation/:id">
              <Invocation userCache={userCache} />
            </Route>

            <Route path="/admin/disliked-cards">
              <DislikedCards userCache={userCache} />
            </Route>

            <Route path="/admin/importNovelTracking">
              <ImportNovelTracking />
            </Route>

            {/* <Route exact path="/admin/documents">
              <Documents users={users} />
            </Route> */}

            <Route
              exact
              path="/admin/document/:id"
            >
              <Document userCache={userCache} />
            </Route>

            <Route path="/admin/likes">
              <Likes userCache={userCache} />
            </Route>

            <Route
              exact
              path={`/admin/canvas/:projectId`}
            >
              <Canvas readOnly={true} />
            </Route>

            <Route
              exact
              path="/admin/integrationTests"
            >
              <IntegrationTests />
            </Route>
            <Route
              exact
              path="/admin/logs"
            >
              <AdminLogs />
            </Route>
            <Route
              exact
              path="/admin/brainstorm-tester"
            >
              <BrainstormTester />
            </Route>
            <Route
              exact
              path="/admin/image-gen-tester"
            >
              <ImageGenTester />
            </Route>
            <Route
              exact
              path="/admin/character-avatar-tester"
            >
              <Redirect to="/admin/image-gen-tester?mode=character" />
            </Route>
            <Route
              exact
              path="/admin/shared-documents"
            >
              <SharedDocumentsLookup />
            </Route>
            <Route
              exact
              path="/admin/stream-replay"
            >
              <StreamReplayPage />
            </Route>
            <Route
              exact
              path="/admin/duplicate-stripe-accounts"
            >
              <DuplicatedStripeAccounts />
            </Route>
            <Route
              exact
              path="/admin/model-credit-markup"
            >
              <ModelCreditMarkup />
            </Route>
            <Route
              exact
              path="/admin/seo-token-usage"
            >
              <SeoTokenUsage />
            </Route>
            <Route
              exact
              path="/admin/discord-upload"
            >
              <DiscordUpload />
            </Route>
            <Route
              exact
              path="/admin/plugin-repair"
            >
              <PluginRepair />
            </Route>
            <Route
              exact
              path="/admin/feedback-document-split"
            >
              <FeedbackDocumentSplit />
            </Route>
            <Route
              exact
              path="/admin/experiments/:experimentId"
            >
              <ExperimentDetail />
            </Route>
            <Route
              exact
              path="/admin/experiments"
            >
              <Experiments />
            </Route>
            <Route
              exact
              path="/admin/facebook-deletion"
            >
              <FacebookDeletionTester />
            </Route>
            <Route
              exact
              path="/admin/chat-feedback"
            >
              <AgenticChatFeedback />
            </Route>
            <Route
              exact
              path="/admin/chat-bug-reports"
            >
              <AgenticChatBugReports />
            </Route>
            <Route exact path="/admin/chat-controls"><ChatControls /></Route>
            <Route
              exact
              path="/admin/chat-experiments"
            >
              <ChatExperiments />
            </Route>
            <Route
              exact
              path="/admin/chat-experience-survey"
            >
              <ChatExperienceSurvey />
            </Route>
            <Route
              exact
              path="/admin/cli-api-keys"
            >
              <React.Suspense fallback={<div>Loading...</div>}>
                <CliApiKeys />
              </React.Suspense>
            </Route>
          </Switch> : <p>You do not have permission to view this page.</p>}
        </div>
      </div>

      {/* Command Menu */}
      <CommandMenu
        isOpen={commandMenuOpen}
        onClose={() => setCommandMenuOpen(false)}
        navItems={navItems}
        can={can}
        navigate={navigate}
      />
    </>
  );
}

// This wrapper component manages authentication and passes the user to Admin
export default function AdminWrapper() {
  const params = useParams();
  const history = useHistory();
  const location = useLocation();
  const [initialized, setInitialized] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { authUid, can } = useAdminAccess();
  const canLogView = authUid === user?.uid && can("admin.access");

  // Handle authentication
  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((userAuth) => {
      if (userAuth?.isAnonymous) {
        setUser(null);
      } else {
        setUser(userAuth);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Track admin page views
  useEffect(() => {
    // Wait for capabilities for this identity before logging an admin view.
    if (!canLogView) return;

    // Track initial page view
    if (!initialized) {
      logAdminView(location.pathname);
      setInitialized(true);
    }

    // Track subsequent navigation
    const unlisten = history.listen((location) => {
      if (user) {
        logAdminView(location.pathname);
      }
    });

    return () => {
      unlisten();
    };
  }, [history, location, initialized, user, canLogView]);

  if (loading) {
    return <CenterBox>Loading...</CenterBox>;
  }

  // Don't render Admin component if user is null
  if (!user) {
    return <Redirect to="/" />;
  }

  return (
    <Admin
      user={user}
      match={{ params }}
    />
  );
}
